import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Briefcase, Eye, TrendingUp, Clock, CheckCircle2, XCircle, FileText } from 'lucide-react';

export default function CandidateDashboard() {
  const stats = [
    { label: 'Active Applications', value: '8', icon: FileText, color: 'text-blue-600' },
    { label: 'Interviews Scheduled', value: '3', icon: Clock, color: 'text-purple-600' },
    { label: 'Profile Views', value: '24', icon: Eye, color: 'text-green-600' },
    { label: 'Offers Received', value: '1', icon: CheckCircle2, color: 'text-yellow-600' },
  ];

  const applications = [
    {
      id: '1',
      job: 'Senior Software Engineer',
      company: 'Tech Corp',
      appliedDate: '2025-11-01',
      status: 'interview' as const,
      aiScore: 92,
      aiExplanation: 'Strong technical skills match (95%). Your 8 years of experience exceeds the 5+ years requirement. Education qualifications met.',
    },
    {
      id: '2',
      job: 'Full Stack Developer',
      company: 'StartupXYZ',
      appliedDate: '2025-10-28',
      status: 'under_review' as const,
      aiScore: 88,
      aiExplanation: 'Excellent skills alignment (92%). Relevant project experience in similar domains. All technical requirements satisfied.',
    },
    {
      id: '3',
      job: 'Product Manager',
      company: 'Big Corp',
      appliedDate: '2025-10-25',
      status: 'shortlisted' as const,
      aiScore: 85,
      aiExplanation: 'Good leadership experience (4 years). Strong communication skills demonstrated in previous roles.',
    },
    {
      id: '4',
      job: 'DevOps Engineer',
      company: 'Cloud Solutions',
      appliedDate: '2025-10-20',
      status: 'rejected' as const,
      aiScore: 65,
      aiExplanation: 'Limited experience with required cloud platforms (AWS, Azure). Consider gaining certifications.',
    },
  ];

  const recommendedJobs = [
    { id: '1', title: 'Lead Software Engineer', company: 'Innovation Labs', match: 94 },
    { id: '2', title: 'Senior Backend Developer', company: 'Scale Inc', match: 91 },
    { id: '3', title: 'Technical Lead', company: 'Future Tech', match: 89 },
  ];

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      submitted: 'default',
      under_review: 'info',
      shortlisted: 'warning',
      interview: 'success',
      offered: 'success',
      rejected: 'error',
    };
    return variants[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome Back!</h1>
          <p className="text-gray-600 mt-2">Track your applications and discover new opportunities</p>
        </div>

        {/* Stats */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`rounded-lg bg-gray-100 p-3 ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Applications */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>My Applications</CardTitle>
                  <Button size="sm" variant="ghost">View All</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{app.job}</h4>
                          <p className="text-sm text-gray-600">{app.company}</p>
                        </div>
                        <Badge variant={getStatusBadge(app.status)}>
                          {getStatusLabel(app.status)}
                        </Badge>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">AI Match Score</span>
                          <span className="text-lg font-bold text-primary-600">{app.aiScore}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ width: `${app.aiScore}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center">
                          <Eye className="h-3 w-3 mr-1" />
                          AI Explanation (Glass Box)
                        </p>
                        <p className="text-sm text-blue-800">{app.aiExplanation}</p>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>Applied {new Date(app.appliedDate).toLocaleDateString()}</span>
                        <Button size="sm" variant="outline">View Details</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Strength */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Strength</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">85%</div>
                  <p className="text-sm text-gray-600">Complete</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-gray-700">Resume uploaded</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-gray-700">Skills added</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <XCircle className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-500">Portfolio link missing</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4">
                  Complete Profile
                </Button>
              </CardContent>
            </Card>

            {/* Recommended Jobs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-primary-600" />
                  Recommended For You
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recommendedJobs.map((job) => (
                    <div key={job.id} className="border border-gray-200 rounded-lg p-3 hover:border-primary-300 transition-colors cursor-pointer">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 text-sm">{job.title}</h5>
                          <p className="text-xs text-gray-600">{job.company}</p>
                        </div>
                        <Badge variant="success" size="sm">{job.match}% match</Badge>
                      </div>
                      <Button size="sm" variant="outline" className="w-full">Apply Now</Button>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" className="w-full mt-3">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Browse All Jobs
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
